import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useElementOnScreen } from "@/hooks/useVideoOnScreen";

import { ChannelPingPipProvider } from "./channel-ping-pip-context";

interface PipProviderProps {
  children: React.ReactNode;
}

type handleIsPipProps = {
  playerRef: React.MutableRefObject<HTMLVideoElement | null>;
  pictureInPictureButton: React.RefObject<HTMLButtonElement | null>;
  initializedPip: React.MutableRefObject<boolean>;
  onPip: ((isPip: boolean) => void) | undefined;
};

type IPipContext = {
  isPip: boolean;
  videoElement: HTMLVideoElement | null;
  handleIsPip: ({
    playerRef,
    pictureInPictureButton,
  }: handleIsPipProps) => void;
  setIsRequestingPipInProgress: React.Dispatch<React.SetStateAction<boolean>>;
  handleStartPip: (newPipElement: HTMLVideoElement) => Promise<void>;
};

const PipContext = createContext<any>({
  videoElement: null,
  isPip: false,
});

const PipProvider: React.FC<PipProviderProps> = ({ children }) => {
  const [isPip, setIsPip] = useState(false);
  const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(
    null,
  );
  const isVideoOnScreen = useElementOnScreen(videoElement);
  const [isRequestingPipInProgress, setIsRequestingPipInProgress] =
    useState(false);

  const handleIsPip = async ({
    playerRef,
    pictureInPictureButton,
    initializedPip,
    onPip,
  }: handleIsPipProps) => {
    if (!playerRef.current || !pictureInPictureButton.current) return;
    try {
      if (playerRef.current !== document.pictureInPictureElement) {
        pictureInPictureButton.current.disabled = true;
        setIsRequestingPipInProgress(true);

        await playerRef.current.requestPictureInPicture();

        setIsRequestingPipInProgress(false);
        setVideoElement(playerRef.current);
        onPip?.(true);
        setIsPip(true);
        initializedPip.current = true;
      } else {
        await document.exitPictureInPicture();

        setVideoElement(null);
        setIsPip(false);
        onPip?.(false);
      }
    } catch (error: any) {
      console.error(
        `An error occurred while trying to switch into picture-in-picture mode: ${error.message} (${error.name})`,
      );
    } finally {
      pictureInPictureButton.current.disabled = false;
    }
  };

  const handleStartPip = async (newPipElement: HTMLVideoElement) => {
    // This flag serves to prevent calling leavepictureinpicture event because it will be triggered by requestPictureInPicture()
    setIsRequestingPipInProgress(true);

    newPipElement.currentTime = videoElement!.currentTime;
    newPipElement.volume = videoElement!.volume;
    newPipElement.muted = videoElement!.muted;
    newPipElement.playbackRate = videoElement!.playbackRate;

    newPipElement.addEventListener(
      "loadeddata",
      async () => {
        if (newPipElement === document.pictureInPictureElement) return;

        await newPipElement
          .requestPictureInPicture()
          .then(() => {
            // cleanup the previous video from DOM
            videoElement!.pause();
            videoElement!.remove();
            setVideoElement(newPipElement);

            newPipElement.play().catch((e) => {
              console.log("Pip automatic start video FAIL", e);
            });
          })
          .catch((e) => {
            console.log("Pip request picture-in-picture mode FAIL", e);
            if (document.pictureInPictureElement) {
              document.exitPictureInPicture();
            }
          })
          .finally(() => {
            setIsRequestingPipInProgress(false);
          });
      },
      { once: true },
    );
  };

  const handleLeavePictureInPicture = () => {
    if (isRequestingPipInProgress) return;

    if (!isVideoOnScreen) {
      videoElement?.pause();
    }
    setIsPip(false);
    setVideoElement(null);
  };

  useEffect(() => {
    if (videoElement) {
      videoElement.addEventListener(
        "leavepictureinpicture",
        handleLeavePictureInPicture,
      );
    }
    return () => {
      if (videoElement) {
        videoElement.removeEventListener(
          "leavepictureinpicture",
          handleLeavePictureInPicture,
        );
      }
    };
  }, [videoElement, isRequestingPipInProgress, isVideoOnScreen]);

  const value = useMemo(
    () => ({
      isPip,
      videoElement,
      handleIsPip,
      setIsRequestingPipInProgress,
      handleStartPip,
    }),
    [isPip, videoElement, handleStartPip],
  );

  return (
    <PipContext.Provider value={value}>
      <ChannelPingPipProvider videoElement={videoElement}>
        {children}
      </ChannelPingPipProvider>
    </PipContext.Provider>
  );
};

const usePipContext = (): IPipContext => useContext(PipContext);

export { PipProvider, usePipContext, type IPipContext };
